import {AhpErrorCodes, JsonRpcErrorCodes} from '@microsoft/agent-host-protocol'
import {RpcError} from '@microsoft/agent-host-protocol/client'

import {MissionControlError} from './mission-control/mission-control-environment-client'
import {asAhpVersionMismatch} from './protocol-support'
import {ProjectProvisioningError} from './session-host/resolve-project'

export type RelayFailure =
  | {readonly kind: 'cancelled'}
  | {readonly kind: 'transport'; readonly reason: 'no-response' | 'host-closed'}
  | {readonly kind: 'http'; readonly status: number; readonly code?: string}
  | {
      readonly kind: 'rpc'
      readonly code: number
      readonly subtype?: 'cliVersionMismatch' | 'sessionNotFound' | 'authenticateUnavailable'
    }
  | {readonly kind: 'protocol-version'}
  | {readonly kind: 'wake-timeout'}
  | {readonly kind: 'resumption-timeout'}
  | {readonly kind: 'provisioning'; readonly reason: ProjectProvisioningError['reason']}
  | {readonly kind: 'unknown'}

export type RelayFailurePresentation =
  | 'generic'
  | 'compute-unavailable'
  | 'session-unavailable'
  | 'environment-unreachable'
  | 'license-required'
  | 'runtime-update'
  | 'wake-timeout'

export interface RelayFailureDisposition {
  readonly automaticRetry: 'retry' | 'stop' | 'ignore'
  readonly manualRetry: boolean
  readonly presentation: RelayFailurePresentation
}

interface ErrorFacts {
  failure?: unknown
  status?: unknown
  code?: unknown
  hasResponse?: unknown
  requestFailure?: {kind?: unknown; reason?: unknown}
}

function frozen<T extends RelayFailure>(failure: T): T {
  return Object.freeze(failure)
}

function isAbortError(error: unknown): boolean {
  return !!error && typeof error === 'object' && 'name' in error && error.name === 'AbortError'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object'
}

const missingProperty = Symbol('missing-property')
const invalidProperty = Symbol('invalid-property')

function readEnumerableOwnDataProperty(
  value: object,
  key: string,
): unknown | typeof missingProperty | typeof invalidProperty {
  const descriptor = Object.getOwnPropertyDescriptor(value, key)
  if (!descriptor) return missingProperty
  if (!descriptor.enumerable || !('value' in descriptor)) return invalidProperty
  return descriptor.value
}

function relayFailureFromUnknown(value: unknown): RelayFailure | undefined {
  if (!isRecord(value)) return undefined
  const prototype = Object.getPrototypeOf(value)
  if (prototype !== Object.prototype && prototype !== null) return undefined
  const kind = readEnumerableOwnDataProperty(value, 'kind')
  switch (kind) {
    case 'cancelled':
    case 'protocol-version':
    case 'wake-timeout':
    case 'resumption-timeout':
    case 'unknown':
      return frozen({kind})
    case 'transport': {
      const reason = readEnumerableOwnDataProperty(value, 'reason')
      return reason === 'no-response' || reason === 'host-closed' ? frozen({kind: 'transport', reason}) : undefined
    }
    case 'http': {
      const status = readEnumerableOwnDataProperty(value, 'status')
      if (typeof status !== 'number' || !Number.isFinite(status)) {
        return undefined
      }
      const code = readEnumerableOwnDataProperty(value, 'code')
      return code === missingProperty || code === undefined || typeof code === 'string'
        ? frozen({kind: 'http', status, ...(typeof code === 'string' ? {code} : {})})
        : undefined
    }
    case 'rpc': {
      const code = readEnumerableOwnDataProperty(value, 'code')
      if (typeof code !== 'number' || !Number.isFinite(code)) {
        return undefined
      }
      const subtype = readEnumerableOwnDataProperty(value, 'subtype')
      return subtype === missingProperty ||
        subtype === undefined ||
        subtype === 'cliVersionMismatch' ||
        subtype === 'sessionNotFound' ||
        subtype === 'authenticateUnavailable'
        ? frozen({kind: 'rpc', code, ...(typeof subtype === 'string' ? {subtype} : {})})
        : undefined
    }
    case 'provisioning': {
      const reason = readEnumerableOwnDataProperty(value, 'reason')
      return reason === 'unsupported' || reason === 'clone-failed' || reason === 'timeout' || reason === 'closed'
        ? frozen({kind: 'provisioning', reason})
        : undefined
    }
    default:
      return undefined
  }
}

function rpcSubtype(error: RpcError): 'cliVersionMismatch' | undefined {
  const data = error.data
  return error.code === JsonRpcErrorCodes.InternalError &&
    data &&
    typeof data === 'object' &&
    'kind' in data &&
    data.kind === 'cliVersionMismatch'
    ? 'cliVersionMismatch'
    : undefined
}

export function relayFailureFromError(error: unknown): RelayFailure {
  if (isRecord(error) && 'failure' in error) {
    const nested = (error as ErrorFacts).failure
    const failure = relayFailureFromUnknown(nested)
    if (failure) return failure
  }
  if (asAhpVersionMismatch(error)) return frozen({kind: 'protocol-version'})
  if (error instanceof ProjectProvisioningError) return frozen({kind: 'provisioning', reason: error.reason})
  if (error instanceof MissionControlError && error.code === 'wake_timeout') return frozen({kind: 'wake-timeout'})
  if (error instanceof RpcError) {
    const subtype = rpcSubtype(error)
    return frozen({kind: 'rpc', code: error.code, ...(subtype ? {subtype} : {})})
  }

  if (error && typeof error === 'object') {
    const facts = error as ErrorFacts
    if (facts.hasResponse === false && facts.requestFailure?.kind === 'no-response') {
      return facts.requestFailure.reason === 'aborted'
        ? frozen({kind: 'cancelled'})
        : frozen({kind: 'transport', reason: 'no-response'})
    }
    if (typeof facts.status === 'number') {
      return frozen({
        kind: 'http',
        status: facts.status,
        ...(typeof facts.code === 'string' ? {code: facts.code} : {}),
      })
    }
  }
  if (isAbortError(error)) return frozen({kind: 'cancelled'})
  return frozen({kind: 'unknown'})
}

export function relayFailureDisposition(failure: RelayFailure): RelayFailureDisposition {
  switch (failure.kind) {
    case 'cancelled':
      return {automaticRetry: 'ignore', manualRetry: false, presentation: 'generic'}
    case 'transport':
      // A host that keeps ending connections would end the next one too.
      return failure.reason === 'host-closed'
        ? {automaticRetry: 'stop', manualRetry: true, presentation: 'generic'}
        : {automaticRetry: 'retry', manualRetry: true, presentation: 'generic'}
    case 'wake-timeout':
      return {automaticRetry: 'retry', manualRetry: true, presentation: 'wake-timeout'}
    case 'resumption-timeout':
      return {automaticRetry: 'stop', manualRetry: true, presentation: 'generic'}
    case 'protocol-version':
      return {automaticRetry: 'stop', manualRetry: false, presentation: 'runtime-update'}
    case 'provisioning':
      return failure.reason === 'closed'
        ? {automaticRetry: 'retry', manualRetry: true, presentation: 'generic'}
        : {automaticRetry: 'stop', manualRetry: true, presentation: 'generic'}
    case 'rpc':
      if (failure.code === AhpErrorCodes.Conflict || failure.subtype === 'authenticateUnavailable') {
        return {automaticRetry: 'retry', manualRetry: true, presentation: 'generic'}
      }
      if (failure.code === AhpErrorCodes.NotFound && failure.subtype === 'sessionNotFound') {
        return {automaticRetry: 'stop', manualRetry: false, presentation: 'session-unavailable'}
      }
      return {
        automaticRetry: 'stop',
        manualRetry:
          failure.code !== AhpErrorCodes.UnsupportedProtocolVersion && failure.subtype !== 'cliVersionMismatch',
        presentation:
          failure.code === AhpErrorCodes.UnsupportedProtocolVersion || failure.subtype === 'cliVersionMismatch'
            ? 'runtime-update'
            : 'generic',
      }
    case 'http':
      switch (failure.code) {
        case 'environment_compute_missing':
        case 'environment_compute_terminal':
          return {automaticRetry: 'stop', manualRetry: false, presentation: 'compute-unavailable'}
        case 'environment_unreachable':
          return {automaticRetry: 'stop', manualRetry: true, presentation: 'environment-unreachable'}
        case 'copilot_license_required':
          return {automaticRetry: 'stop', manualRetry: false, presentation: 'license-required'}
        default:
          return failure.status >= 500
            ? {automaticRetry: 'stop', manualRetry: true, presentation: 'generic'}
            : {automaticRetry: 'retry', manualRetry: true, presentation: 'generic'}
      }
    case 'unknown':
      return {automaticRetry: 'retry', manualRetry: true, presentation: 'generic'}
  }
}

export class RelayConnectionError extends Error {
  readonly failure: RelayFailure

  constructor(message: string, failure: RelayFailure, options?: ErrorOptions) {
    super(message, options)
    this.name = 'RelayConnectionError'
    this.failure = failure
  }
}
